/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'

import UiTabSwitcher, { UiTabSwitcherItem } from 'components/Ui/UiTabSwitcher'
import UiSearch from 'components/Ui/UiSearch'
import UiButton from 'components/Ui/UiButton'
import { UiTabVariant } from 'types/enums/UiTabVariant'
import { UiButtonSize } from 'types/enums/UiButtonSize'
import { UiButtonStyle } from 'types/enums/UiButtonStyle'

interface UiPageHeaderToolbarProps {
    tabs?: UiTabSwitcherItem[]
    selectedTabId?: string | number
    onTabChange?: (id: string | number) => void
    searchValue?: string
    onSearchChange?: (value: string) => void
    searchPlaceholder?: string
    searchInputId?: string
    actionTitle?: string
    onActionClick?: () => void
    actionIcon?: React.ReactNode
    actionId?: string
    resetAction?: React.ReactNode
    firstRowRightExtra?: React.ReactNode
    tabsLeftExtra?: React.ReactNode
    tabsRightExtra?: React.ReactNode
    exportActions?: React.ReactNode
    rightExtra?: React.ReactNode
    title?: string
    tabsVariant?: UiTabVariant
}

const UiPageHeaderToolbar = (props: UiPageHeaderToolbarProps) => {
    const showSearch = !!props.onSearchChange
    const { onTabChange } = props
    const handleTabChange = React.useCallback(
        (id: string | number) => {
            onTabChange?.(id)

            if (typeof window !== 'undefined') {
                window.dispatchEvent(
                    new CustomEvent('table:reset-pagination-first-page')
                )
            }
        },
        [onTabChange]
    )

    if (!showSearch) {
        return (
            <div className="UiPageHeaderToolbar w-full flex flex-col lg:flex-row items-center justify-between gap-4">
                <div className="w-full lg:w-auto flex items-center gap-3 overflow-x-auto no-scrollbar">
                    {props.title && (
                        <div className="text-xl font-bold mr-4">
                            {props.title}
                        </div>
                    )}
                    {props.tabsLeftExtra}
                    {props.tabs && (
                        <UiTabSwitcher
                            items={props.tabs}
                            selected={props.selectedTabId || ''}
                            onClick={handleTabChange}
                            variant={props.tabsVariant}
                        />
                    )}
                    {props.tabsRightExtra}
                </div>

                <div className="w-full lg:w-auto flex items-center gap-3 justify-end ml-auto flex-wrap">
                    {props.rightExtra}
                    {props.firstRowRightExtra}
                    {props.exportActions}
                    {props.resetAction}
                    {props.actionTitle && props.onActionClick && (
                        <UiButton
                            id={props.actionId}
                            icon={props.actionIcon}
                            size={UiButtonSize.Normal}
                            style={UiButtonStyle.Primary}
                            onClick={props.onActionClick}
                            title={props.actionTitle}
                        />
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="UiPageHeaderToolbar w-full flex flex-col gap-y-4">
            {/* Row 1: Searchbar on left, Reset and Add on right */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="w-full md:w-auto flex items-center gap-3">
                    {props.title && (
                        <div className="text-xl font-bold mr-4">
                            {props.title}
                        </div>
                    )}
                    {props.onSearchChange && (
                        <UiSearch
                            value={props.searchValue || ''}
                            onChange={props.onSearchChange}
                            placeholder={props.searchPlaceholder || ''}
                            inputId={props.searchInputId}
                            fullWidth={false}
                        />
                    )}
                </div>

                <div className="w-full md:w-auto flex items-center gap-3 justify-end ml-auto flex-wrap">
                    {props.firstRowRightExtra}
                    {props.resetAction}
                    {props.actionTitle && props.onActionClick && (
                        <UiButton
                            id={props.actionId}
                            icon={props.actionIcon}
                            size={UiButtonSize.Normal}
                            style={UiButtonStyle.Primary}
                            onClick={props.onActionClick}
                            title={props.actionTitle}
                        />
                    )}
                </div>
            </div>

            {/* Row 2: Tabs on left, Exports on right */}
            {(props.tabs ||
                props.tabsLeftExtra ||
                props.exportActions ||
                props.rightExtra) && (
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="w-full md:w-auto flex items-center gap-3">
                        {props.tabsLeftExtra}
                        {props.tabs && (
                            <UiTabSwitcher
                                items={props.tabs}
                                selected={props.selectedTabId || ''}
                                onClick={handleTabChange}
                                variant={props.tabsVariant}
                            />
                        )}
                        {props.tabsRightExtra}
                    </div>

                    <div className="w-full md:w-auto flex items-center gap-3 justify-end ml-auto flex-wrap">
                        {props.exportActions}
                        {props.rightExtra}
                    </div>
                </div>
            )}
        </div>
    )
}

export default UiPageHeaderToolbar
